import { useState, useEffect } from 'react';
import { client } from '../../api/client';
import { Pagination } from '../common/Pagination';
import { useToast } from '../common/Toast';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  type: 'fizicko_lice' | 'pravno_lice';
  role: 'user' | 'admin';
  company_name: string | null;
  maticni_broj: string | null;
  tokens: number;
  is_suspended: boolean;
  email_verified: boolean;
  created_at: string;
}

export function UsersTab() {
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [tokensModalOpen, setTokensModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form States
  const [messageText, setMessageText] = useState('');
  const [tokenAmount, setTokenAmount] = useState(0);
  const [editForm, setEditForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    type: '', 
    role: '', 
    company_name: '',
    maticni_broj: '',
    newPassword: '',
    email_verified: false
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.api.fetch('/admin/users');
      const data = await res.json();
      if (data.success) {
        setUsers(data.data || []);
      } else {
        setError(data.error || 'Greška pri učitavanju korisnika');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.type === roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenMessage = (user: User) => {
    setSelectedUser(user);
    setMessageText('');
    setMessageModalOpen(true);
  };

  const handleSendMessage = async () => {
    if (!selectedUser || !messageText) return;

    try {
      const res = await client.api.fetch(`/admin/users/${selectedUser.id}/message`, {
        method: 'POST',
        body: JSON.stringify({ message: messageText })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Poruka poslata', 'Poruka uspešno poslata korisniku!');
        setMessageModalOpen(false);
        setSelectedUser(null);
        setMessageText('');
      } else {
        toast.error('Greška', data.error || 'Greška pri slanju poruke');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleOpenTokens = (user: User) => {
    setSelectedUser(user);
    setTokenAmount(0);
    setTokensModalOpen(true);
  };

  const handleManageTokens = async () => {
    if (!selectedUser || tokenAmount === 0) return;

    try {
      const res = await client.api.fetch(`/admin/users/${selectedUser.id}/tokens`, {
        method: 'POST',
        body: JSON.stringify({ amount: tokenAmount })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === selectedUser.id ? { ...u, tokens: u.tokens + tokenAmount } : u));
        setTokensModalOpen(false);
        setSelectedUser(null);
        setTokenAmount(0);
        toast.success('Uspešno', `Tokeni su ${tokenAmount > 0 ? 'dodati' : 'oduzeti'} korisniku.`);
      } else {
        toast.error('Greška', data.error || 'Greška pri upravljanju tokenima');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleOpenEdit = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      type: user.type || 'fizicko_lice',
      role: user.role || 'user',
      company_name: user.company_name || '',
      maticni_broj: user.maticni_broj || '',
      newPassword: '',
      email_verified: user.email_verified || false
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      const payload: any = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        type: editForm.type,
        role: editForm.role,
        email_verified: editForm.email_verified
      };
      
      // Dodaj lozinku samo ako je unesena
      if (editForm.newPassword && editForm.newPassword.trim() !== '') {
        payload.password = editForm.newPassword;
      }
      
      // Dodaj podatke za pravna lica
      if (editForm.type === 'pravno_lice') {
        payload.company_name = editForm.company_name;
        payload.maticni_broj = editForm.maticni_broj;
      }

      const res = await client.api.fetch(`/admin/users/${selectedUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === selectedUser.id ? {
          ...u,
          name: editForm.name,
          email: editForm.email,
          phone: editForm.phone,
          type: editForm.type as any,
          role: editForm.role as any,
          company_name: editForm.company_name || null,
          maticni_broj: editForm.maticni_broj || null,
          email_verified: editForm.email_verified
        } : u));
        setEditModalOpen(false);
        setSelectedUser(null);
        toast.success('Uspešno', 'Korisnik je izmenjen.');
      } else {
        toast.error('Greška', data.error || 'Greška pri izmeni korisnika');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleVerifyUser = async (user: User) => {
    if (user.email_verified) {
      toast.info('Info', 'Korisnik je već verifikovan.');
      return;
    }

    try {
      const res = await client.api.fetch(`/admin/users/${user.id}/verify`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === user.id ? { ...u, email_verified: true } : u));
        toast.success('Uspešno', 'Korisnik je verifikovan.');
      } else {
        toast.error('Greška', data.error || 'Greška pri verifikaciji korisnika');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleSuspendUser = async (user: User) => {
    const reason = prompt('Unesite razlog suspenzije:');
    if (!reason) return;

    try {
      const res = await client.api.fetch(`/admin/users/${user.id}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === user.id ? { ...u, is_suspended: true } : u));
        toast.success('Uspešno', 'Korisnik je suspendovan.');
      } else {
        toast.error('Greška', data.error || 'Greška pri suspendovanju korisnika');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleUnsuspendUser = async (user: User) => {
    try {
      const res = await client.api.fetch(`/admin/users/${user.id}/unsuspend`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setUsers(users.map(u => u.id === user.id ? { ...u, is_suspended: false } : u));
        toast.success('Uspešno', 'Korisnik je reaktiviran.');
      } else {
        toast.error('Greška', data.error || 'Greška pri reaktivaciji korisnika');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju sa serverom');
    }
  };

  const handleDeleteUser = async (user: User) => {
    toast.showConfirm({
      title: 'Brisanje korisnika',
      message: `Da li ste sigurni da želite da obrišete korisnika "${user.name}"? Ova akcija se ne može poništiti.`,
      confirmText: 'Obriši',
      cancelText: 'Otkaži',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await client.api.fetch(`/admin/users/${user.id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            setUsers(users.filter(u => u.id !== user.id));
            toast.success('Uspešno', 'Korisnik je obrisan.');
          } else {
            toast.error('Greška', data.error || 'Greška pri brisanju korisnika');
          }
        } catch (err) {
          toast.error('Greška', 'Greška pri povezivanju sa serverom');
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
          <button onClick={loadUsers} className="ml-4 underline">Pokušaj ponovo</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Pretraži korisnike..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        >
          <option value="all">Sve uloge</option>
          <option value="admin">Admin</option>
          <option value="fizicko_lice">Fizičko lice</option>
          <option value="pravno_lice">Pravno lice</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 dark:bg-slate-700/50">
              <tr>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Korisnik</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Email</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Tip naloga</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Uloga</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Tokeni</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Status</th>
                <th className="p-4 font-semibold text-slate-900 dark:text-white">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500 dark:text-slate-400">
                    Nema korisnika koji odgovaraju filterima.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                    <td className="p-4">
                      <div className="font-medium text-slate-900 dark:text-white">{user.name}</div>
                      {user.company_name && (
                        <div className="text-sm text-slate-500">{user.company_name}</div>
                      )}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-400">
                      {user.email}
                      {!user.email_verified && (
                        <span className="ml-2 text-xs text-yellow-600">(neverifikovan)</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.type === 'pravno_lice'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {user.type === 'pravno_lice' ? 'Pravno lice' : 'Fizičko lice'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-300'
                      }`}>
                        {user.role === 'admin' ? 'Administrator' : 'Korisnik'}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {user.tokens}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_suspended
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      }`}>
                        {user.is_suspended ? 'Suspendovan' : 'Aktivan'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleOpenEdit(user)}
                          className="text-indigo-600 hover:text-indigo-500 font-medium text-sm"
                        >
                          Izmeni
                        </button>
                        <button
                          onClick={() => handleOpenMessage(user)}
                          className="text-slate-600 dark:text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 font-medium text-sm"
                        >
                          Poruka
                        </button>
                        <button
                          onClick={() => handleOpenTokens(user)}
                          className="text-yellow-600 hover:text-yellow-500 font-medium text-sm"
                        >
                          Tokeni
                        </button>
                        {!user.email_verified && (
                          <button
                            onClick={() => handleVerifyUser(user)}
                            className="text-teal-600 hover:text-teal-500 font-medium text-sm"
                          >
                            Verifikuj
                          </button>
                        )}
                        {user.is_suspended ? (
                          <button
                            onClick={() => handleUnsuspendUser(user)}
                            className="text-green-600 hover:text-green-500 font-medium text-sm"
                          >
                            Reaktiviraj
                          </button>
                        ) : (
                          <button
                            onClick={() => handleSuspendUser(user)}
                            className="text-orange-600 hover:text-orange-500 font-medium text-sm"
                          >
                            Suspenduj
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-500 font-medium text-sm"
                        >
                          Obriši
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          totalItems={filteredUsers.length}
        />
      </div>

      {/* Message Modal */}
      {messageModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Pošalji poruku: {selectedUser.name}</h3>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-4 h-32 resize-none"
              placeholder="Unesite poruku..."
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setMessageModalOpen(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Pošalji
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tokens Modal */}
      {tokensModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Upravljanje tokenima: {selectedUser.name}</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">Trenutno stanje: {selectedUser.tokens} tokena</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Dodaj/Oduzmi tokene (koristite minus za oduzimanje)
              </label>
              <input
                type="number"
                value={tokenAmount}
                onChange={(e) => setTokenAmount(parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTokensModalOpen(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={handleManageTokens}
                disabled={tokenAmount === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Izmeni korisnika: {selectedUser.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Ime</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nova lozinka (ostavite prazno za nepromenjeno)</label>
                <input
                  type="password"
                  value={editForm.newPassword}
                  onChange={(e) => setEditForm({ ...editForm, newPassword: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tip korisnika</label>
                <select
                  value={editForm.type}
                  onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="fizicko_lice">Fizičko lice</option>
                  <option value="pravno_lice">Pravno lice</option>
                </select>
              </div>
              {editForm.type === 'pravno_lice' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Naziv kompanije</label>
                    <input
                      type="text"
                      value={editForm.company_name}
                      onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Matični broj</label>
                    <input
                      type="text"
                      value={editForm.maticni_broj}
                      onChange={(e) => setEditForm({ ...editForm, maticni_broj: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Uloga</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                >
                  <option value="user">Korisnik</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="emailVerified"
                  checked={editForm.email_verified}
                  onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                  className="w-4 h-4 text-amber-500 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-amber-500"
                />
                <label htmlFor="emailVerified" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Email verifikovan
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Otkaži
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={!editForm.name || !editForm.email}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                Sačuvaj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
